'use client'

import { memo } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { NinxlScrollReveal } from '@/app/ninxl/_components/ninxl-scroll-reveal'
import { NinkVoiceRibbon } from './nink-voice-ribbon'

/**
 * baby: Raw speech vs typed result: filler words are marked so we can
 * render them muted on the "you say" side and dropped on the typed side.
 */
type SpokenToken = {
  text: string
  filler?: boolean
}

const SPOKEN_TOKENS: SpokenToken[] = [
  { text: 'uhm so', filler: true },
  { text: 'can you tell the team the' },
  { text: 'uh the', filler: true },
  { text: 'new build is ready,' },
  { text: 'I think', filler: true },
  { text: "the installer's fixed so" },
  { text: 'like', filler: true },
  { text: 'just download it again and' },
  { text: 'yeah', filler: true },
  { text: 'ping me if anything breaks' },
]

const TYPED_RESULT =
  'Can you tell the team the new build is ready? The installer is fixed: download it again and ping me if anything breaks.'

/** baby: Rough words-per-minute reality: keyboard vs speech */
const SPEED_ROWS = [
  { id: 'keyboard', label: 'Keyboard', wpm: 40, width: '27%', accent: false },
  { id: 'nink', label: 'Voice with NINK', wpm: 150, width: '100%', accent: true },
] as const

const SpeedBar = memo(function SpeedBar({
  label,
  wpm,
  width,
  accent,
  reduceMotion,
}: {
  label: string
  wpm: number
  width: string
  accent: boolean
  reduceMotion: boolean
}) {
  return (
    <div className="flex flex-col gap-1.5">
        <div className="flex items-baseline justify-between gap-2">
        <span className="nink-hybrid-body text-sm font-semibold ninxl-theme-text">{label}</span>
        <span className="nink-hybrid-status">~{wpm} WPM</span>
      </div>
      <div className="nink-hybrid-pill h-2.5 w-full overflow-hidden bg-[color:var(--ninxl-8bit-dim)]/40">
        <motion.div
          className={cn(
            'nink-hybrid-pill h-full',
            accent
              ? 'bg-[color:var(--ninxl-brand-teal)]'
              : 'bg-[color:var(--ninxl-8bit-muted)]/70',
          )}
          initial={reduceMotion ? { width } : { width: 0 }}
          whileInView={{ width }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        />
      </div>
    </div>
  )
})

/**
 * baby: Hybrid section: messy speech in, clean text out, plus the
 * keyboard-vs-voice speed gap. Soft cards, pixel labels as accents.
 */
export const NinkFlowCompareSection = memo(function NinkFlowCompareSection() {
  const reduceMotion = Boolean(useReducedMotion())

  return (
    <div className="mx-auto max-w-5xl px-6">
      <NinxlScrollReveal>
        <div className="mb-10 text-center">
          <div className="nink-hybrid-eyebrow mb-3 justify-center">
            Speak messy · send clean
          </div>
          <h2 className="nink-hybrid-display ninxl-theme-text mx-auto mb-4 max-w-2xl text-2xl md:text-4xl">
            Your voice is faster than your keyboard.
          </h2>
          <p className="nink-hybrid-body mx-auto max-w-xl text-sm md:text-base">
            Most people type around 40 words a minute and speak around 150. NINK closes that gap.
            ramble once, and finished text lands at your cursor.
          </p>
        </div>
      </NinxlScrollReveal>

      {/* Angled raw → waveform → cleaned ribbon (pay rotate + screenshot metaphor) */}
      <NinxlScrollReveal delay={0.04} variant="rise" className="mb-6">
        <NinkVoiceRibbon />
      </NinxlScrollReveal>

      {/* Before / after: raw speech vs what NINK types */}
      <NinxlScrollReveal delay={0.08}>
        <div className="grid gap-4 md:grid-cols-2">
          <article className="nink-hybrid-card flex flex-col gap-3 p-5 md:p-6">
            <div className="flex items-center gap-2">
              <span
                className="ninxl-8bit-blink h-2 w-2 rounded-full bg-[color:var(--ninxl-8bit-muted)]"
                aria-hidden
              />
              <span className="nink-hybrid-eyebrow !tracking-[0.12em]">You say</span>
            </div>
            <p className="nink-hybrid-body text-sm leading-relaxed md:text-base">
              {SPOKEN_TOKENS.map((token, index) => (
                <span
                  key={`${token.text}-${index}`}
                  className={cn(
                    token.filler ? 'opacity-40' : 'ninxl-theme-text',
                  )}
                >
                  {token.text}
                  {index < SPOKEN_TOKENS.length - 1 ? ' ' : ''}
                </span>
              ))}
            </p>
            <div className="nink-hybrid-status mt-auto">⇧ Hold · speak · release</div>
          </article>

          <article className="nink-hybrid-card flex flex-col gap-3 p-5 md:p-6">
            <div className="flex items-center gap-2">
              <span
                className="h-2 w-2 rounded-full bg-[color:var(--ninxl-brand-teal)]"
                aria-hidden
              />
              <span className="nink-hybrid-eyebrow !tracking-[0.12em]">NINK types</span>
            </div>
            <p className="nink-hybrid-body ninxl-theme-text text-sm leading-relaxed md:text-base">
              {TYPED_RESULT}
              <span
                className="ninxl-8bit-blink ml-0.5 inline-block h-3.5 w-0.5 translate-y-0.5 bg-[color:var(--ninxl-brand-teal)]"
                aria-hidden
              />
            </p>
            <div className="nink-hybrid-status mt-auto">Pasted at your cursor · on-device</div>
          </article>
        </div>
      </NinxlScrollReveal>

      {/* Speed gap: keyboard vs voice */}
      <NinxlScrollReveal delay={0.1}>
        <div className="nink-hybrid-card mx-auto mt-6 flex max-w-3xl flex-col gap-4 p-5 md:p-6">
          {SPEED_ROWS.map((row) => (
            <SpeedBar
              key={row.id}
              label={row.label}
              wpm={row.wpm}
              width={row.width}
              accent={row.accent}
              reduceMotion={reduceMotion}
            />
          ))}
          <p className="nink-hybrid-status text-center">
            Roughly 3× faster · processed locally
          </p>
        </div>
      </NinxlScrollReveal>
    </div>
  )
})
